import "./TaxCodeMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  TAX CODE MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function TaxCodeMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedTaxCode, setSelectedTaxCode] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [taxCodes, setTaxCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    tax_code: "",
    description: "",
    tax_rate: "",
    is_active: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /tax-code/list/all ───────────────────────────────────
  const getTaxCodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/tax-code/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedTaxCodes = data.map((item) => ({
        id: item.id || item.tax_id,
        tax_code: item.tax_code,
        description: item.description,
        tax_rate: item.tax_rate,
        is_active: item.is_active === true || item.is_active === 1,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setTaxCodes(mappedTaxCodes);
    } catch (err) {
      console.error("Failed to load tax codes:", err.response?.data);
      toastError("Failed to load tax codes");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getTaxCodes();
  }, [getTaxCodes]);

  // ─── GET /tax-code/{tax_id} ───────────────────────────────────
  const getTaxCodeById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/tax-code/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.tax_id,
          tax_code: data.tax_code,
          description: data.description,
          tax_rate: data.tax_rate,
          is_active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch tax code:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /tax-code/add ───────────────────────────────────
  const createTaxCode = async (taxData) => {
    try {
      const response = await axios.post(
        `${API_URL}/tax-code/add`,
        {
          tax_code: taxData.tax_code,
          description: taxData.description,
          tax_rate: parseFloat(taxData.tax_rate),
          is_active: taxData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /tax-code/update/{tax_id} ────────────────────────────
  const updateTaxCode = async (id, taxData) => {
    try {
      const response = await axios.put(
        `${API_URL}/tax-code/update/${id}`,
        {
          tax_code: taxData.tax_code,
          description: taxData.description,
          tax_rate: parseFloat(taxData.tax_rate),
          is_active: taxData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────────────────────
  const openEdit = async (taxCode) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedTaxCode(taxCode);
    setEditingId(taxCode.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const taxData = await getTaxCodeById(taxCode.id);
      setFormData({
        tax_code: taxData.tax_code || "",
        description: taxData.description || "",
        tax_rate: taxData.tax_rate || "",
        is_active: taxData.is_active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load tax code details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedTaxCode(null);
    setEditingId(null);
    setFormData({
      tax_code: "",
      description: "",
      tax_rate: "",
      is_active: 1,
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({
      tax_code: "",
      description: "",
      tax_rate: "",
      is_active: 1,
    });
    setFormError("");
    // setSelectedTaxCode(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    // Validation
    if (!formData.tax_code.trim()) {
      const msg = "Tax code is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.description.trim()) {
      const msg = "Description is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.tax_rate || formData.tax_rate < 0) {
      const msg = "Valid tax rate is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }

    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing tax code
        await updateTaxCode(editingId, formData);

        // Update local state
        setTaxCodes((prev) =>
          prev.map((t) =>
            t.id === editingId
              ? {
                  ...t,
                  tax_code: formData.tax_code,
                  description: formData.description,
                  tax_rate: parseFloat(formData.tax_rate),
                  is_active: formData.is_active === 1,
                }
              : t,
          ),
        );

        toastSuccess("Tax code updated successfully.");
      } else {
        // Create new tax code
        await createTaxCode(formData);

        // Refresh the list
        await getTaxCodes();

        toastSuccess("Tax code created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save tax code.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Tax Code Status ──────────────────────────────
  const toggleTaxCodeStatus = async (taxCode) => {
    const newStatus = taxCode.is_active ? 0 : 1;

    try {
      await updateTaxCode(taxCode.id, {
        tax_code: taxCode.tax_code,
        description: taxCode.description,
        tax_rate: taxCode.tax_rate,
        is_active: newStatus,
      });

      // Update local state
      setTaxCodes((prev) =>
        prev.map((t) =>
          t.id === taxCode.id ? { ...t, is_active: newStatus === 1 } : t,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Tax code activated successfully."
          : "Tax code deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update tax code status.";
      toastError(msg);
    }
  };

  // ── Filter tax codes ──
  const filteredTaxCodes = taxCodes.filter(
    (taxCode) =>
      taxCode.tax_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      taxCode.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Format rate ──
  const formatRate = (rate) => {
    return rate.toFixed(2) + "%";
  };

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="tcm-form-page">
        {/* Header with Back Arrow */}
        <div className="tcm-form-header">
          <div className="tcm-form-header-left">
            <button className="tcm-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="tcm-back-icon" />
            </button>
            <div>
              <h2 className="tcm-form-title">
                {isEditMode ? "Edit Tax Code" : "Add Tax Code"}
              </h2>
              <p className="tcm-form-subtitle">
                {isEditMode
                  ? "Update tax code details and status"
                  : "Create a new tax code configuration"}
              </p>
            </div>
          </div>
        </div>

        <div className="tcm-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="tcm-form-grid">
            {/* Tax Code */}
            <div className="tcm-form-group">
              <label>Tax Code *</label>
              <input
                name="tax_code"
                value={formData.tax_code}
                onChange={handleChange}
                placeholder="e.g. GST-18"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {/* Tax Rate */}
            <div className="tcm-form-group">
              <label>Tax Rate (%) *</label>
              <input
                name="tax_rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.tax_rate}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>

            {/* Description */}
            <div className="tcm-form-group tcm-col-full">
              <label>Description *</label>
              <input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter tax code description"
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="tcm-form-group">
                <label>Status</label>
                <div className="tcm-status-toggle">
                  <label className="tcm-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="tcm-slider"></span>
                  </label>
                  <span className="tcm-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="tcm-form-footer">
            <button className="tcm-btn tcm-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="tcm-btn tcm-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Tax Code"
                  : "Save Tax Code"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  TAX CODE LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="tcm-list-page">
      {/* ─── Top Bar ─── */}
      <div className="tcm-page-header">
        <div>
          <h1 className="tcm-page-title">Tax Code Master</h1>
          <p className="tcm-page-subtitle">Manage tax codes and rates</p>
        </div>
        <div className="tcm-page-actions">
          <div className="tcm-search-box">
            <span className="tcm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search tax codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="tcm-btn tcm-btn-primary" onClick={openAdd}>
            + Add Tax Code
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="tcm-table-card">
        <div className="tcm-table-responsive">
          <table className="tcm-table">
            <thead>
              <tr>
                <th className="tcm-col-id">#</th>
                <th className="tcm-col-taxcode">Tax Code</th>
                <th className="tcm-col-description">Description</th>
                <th className="tcm-col-rate">Tax Rate</th>
                <th className="tcm-col-active">Status</th>
                <th className="tcm-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="tcm-empty-row">
                    Loading tax codes...
                  </td>
                </tr>
              ) : filteredTaxCodes.length === 0 ? (
                <tr>
                  <td colSpan="6">
                    <div className="tcm-empty-state">
                      <div className="tcm-empty-icon">🧾</div>
                      <div className="tcm-empty-title">No Tax Codes Found</div>
                      <div className="tcm-empty-desc">
                        {searchTerm
                          ? `No tax codes match "${searchTerm}"`
                          : "Start by adding your first tax code"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTaxCodes.map((taxCode, index) => (
                  <tr key={taxCode.id}>
                    <td className="tcm-col-id">{index + 1}</td>
                    <td className="tcm-col-taxcode">
                      <span className="tcm-taxcode-badge">
                        {taxCode.tax_code}
                      </span>
                    </td>
                    <td className="tcm-col-description">
                      {taxCode.description}
                    </td>
                    <td className="tcm-col-rate">
                      <span className="tcm-rate-badge">
                        {formatRate(taxCode.tax_rate)}
                      </span>
                    </td>
                    <td className="tcm-col-active">
                      <label className="tcm-switch">
                        <input
                          type="checkbox"
                          checked={taxCode.is_active}
                          onChange={() => toggleTaxCodeStatus(taxCode)}
                        />
                        <span className="tcm-slider"></span>
                      </label>
                    </td>
                    <td className="tcm-col-actions">
                      <div className="tcm-action-group">
                        <button
                          className="tcm-action-btn tcm-action-btn-edit"
                          onClick={() => openEdit(taxCode)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="tcm-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
